import { ReactNode, forwardRef, useState, useEffect, useRef } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  controlWrapper,
  disabled,
  errorBorder,
  errorText,
  input,
  inputWrapper,
  label,
  supportingText,
  textDisabled,
  notDisabled,
} from './input.css';
import { Typography } from '../typography/typography';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';

type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'min' | 'max'
> & {
  startIcon?: {
    icon: IconProp;
    onClick?: () => void;
    className?: string;
  };
  endIcon?: {
    icon: IconProp;
    onClick?: () => void;
    className?: string;
  };
  label?: string;
  error?: string;
  supportingText?: string;
  testId?: string;
  endCta?: ReactNode;
  wrapperClassName?: string;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  allowDecimal?: boolean;
  defaultValue?: number;
  precision?: number;
};

const omitKeys: Array<keyof InputProps> = [
  'startIcon',
  'endIcon',
  'supportingText',
  'testId',
  'endCta',
  'onChange',
  'defaultValue',
  'precision',
  'allowDecimal',
];

const roundToPrecision = (value: number, precision: number): number => {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
};

const formatNumberValue = (
  value: number | undefined,
  precision: number,
): string => {
  if (value === undefined || value === null) return '';
  return roundToPrecision(value, precision).toString();
};

const isValidNumberInput = (value: string, allowDecimal: boolean): boolean => {
  if (value === '') return true;
  if (value === '.' || value === ',') return allowDecimal;

  const normalizedValue = value.replace(',', '.');
  const numberRegex = allowDecimal ? /^-?\d*\.?\d*$/ : /^-?\d*$/;

  if (!numberRegex.test(normalizedValue)) return false;

  const dotCount = (normalizedValue.match(/\./g) || []).length;
  if (dotCount > 1) return false;

  return true;
};

const parseInputValue = (
  value: string,
  allowDecimal: boolean,
  precision: number,
): number | undefined => {
  if (!value) return undefined;

  const normalizedValue = value.replace(',', '.');
  const processedValue = normalizedValue.startsWith('.')
    ? '0' + normalizedValue
    : normalizedValue;

  const parsed = allowDecimal
    ? parseFloat(processedValue)
    : parseInt(processedValue, 10);

  if (isNaN(parsed)) return undefined;

  return allowDecimal ? roundToPrecision(parsed, precision) : parsed;
};

const clampValue = (
  val: number,
  min?: number,
  max?: number,
  precision?: number,
) => {
  let result = val;
  if (min !== undefined && result < min) result = min;
  if (max !== undefined && result > max) result = max;
  return precision !== undefined ? roundToPrecision(result, precision) : result;
};

export const InputNumber = forwardRef<HTMLInputElement, InputProps>(
  (p, ref) => {
    const precision = p.precision ?? 2;
    const [displayValue, setDisplayValue] = useState<string>('');
    const focused = useRef(false);

    // While focused, the display belongs to the user's keystrokes; syncing it
    // from p.value here races typing and re-adds erased digits.
    useEffect(() => {
      if (focused.current) return;
      if (p.value !== undefined) {
        setDisplayValue(formatNumberValue(p.value as any, precision));
      } else if (p.defaultValue !== undefined) {
        setDisplayValue(formatNumberValue(p.defaultValue, precision));
      }
    }, [p.value, p.defaultValue, precision]);

    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = ev.target.value;

      if (!isValidNumberInput(inputValue, p.allowDecimal ?? false)) return;

      setDisplayValue(inputValue);

      if (inputValue === '') {
        p.onChange(undefined);
        return;
      }

      // Only call onChange with parsed value, don't restrict input during typing
      const numberValue = parseInputValue(
        inputValue,
        p.allowDecimal ?? false,
        precision,
      );
      if (numberValue !== undefined) {
        const clamped = clampValue(numberValue, p.min, p.max, precision);
        if (clamped !== numberValue) {
          setDisplayValue(formatNumberValue(clamped, precision));
        }
        p.onChange(clamped);
      }
    };

    const handleBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
      focused.current = false;
      const inputValue = ev.target.value.trim();

      if (inputValue === '') {
        if (p.defaultValue !== undefined) {
          const fallback = clampValue(p.defaultValue, p.min, p.max, precision);
          setDisplayValue(formatNumberValue(fallback, precision));
          p.onChange(fallback);
        } else {
          p.onChange(undefined);
        }
      } else {
        const parsed = parseInputValue(
          inputValue,
          p.allowDecimal ?? false,
          precision,
        );
        if (parsed !== undefined) {
          const clamped = clampValue(parsed, p.min, p.max, precision);
          setDisplayValue(formatNumberValue(clamped, precision));
          p.onChange(clamped);
        }
      }

      p.onBlur?.(ev);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const key = event.key;

      if (key === '-' || key === '+') {
        event.preventDefault();
        return;
      }

      if (!p.allowDecimal && (key === '.' || key === ',')) {
        event.preventDefault();
        return;
      }

      if (p.allowDecimal && (key === '.' || key === ',')) {
        if (displayValue.includes('.') || displayValue.includes(',')) {
          event.preventDefault();
          return;
        }
      }

      p.onKeyDownCapture?.(event);
    };

    return (
      <div
        className={clsx(controlWrapper, p.wrapperClassName)}
        data-testid={p.testId}
      >
        {p.label && (
          <div className={sprinkles({ display: 'flex' })}>
            <Typography
              variant="body"
              size="medium"
              className={clsx(
                label,
                p.error && errorText,
                p.disabled && textDisabled,
              )}
            >
              {p.label}
            </Typography>
            {p.required && (
              <Typography
                variant="body"
                size="medium"
                className={clsx(
                  errorText,
                  sprinkles({ paddingTop: 's' }),
                  p.disabled && textDisabled,
                )}
              >
                *
              </Typography>
            )}
          </div>
        )}

        <div
          className={clsx(
            inputWrapper,
            p.error && errorBorder,
            !p.disabled && notDisabled,
            p.disabled && disabled,
          )}
        >
          {p.startIcon && (
            <FontAwesomeIcon
              data-testid="start-icon"
              icon={p.startIcon.icon}
              className={clsx(p.startIcon.className)}
              onClick={p.startIcon.onClick}
            />
          )}

          <input
            ref={ref}
            {..._.omit(p, omitKeys)}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            role="input"
            name="ds-input"
            onFocus={(e) => {
              focused.current = true;
              e.target.select();
            }}
            className={clsx(input, 'inputNumber')}
          />

          {p.endIcon && (
            <FontAwesomeIcon
              data-testid="end-icon"
              icon={p.endIcon.icon}
              className={clsx(p.endIcon.className)}
              onClick={p.endIcon.onClick}
            />
          )}

          {p.endCta}
        </div>

        {(p.supportingText || p.error) && (
          <Typography
            variant="body"
            size="small"
            className={clsx(supportingText, p.error && errorText)}
          >
            {p.error || p.supportingText}
          </Typography>
        )}
      </div>
    );
  },
);

InputNumber.displayName = 'InputNumber';
